import { Plane, ArrowRight, Clock, ArrowLeftRight, ExternalLink, Luggage } from 'lucide-react';

export function FlightCard({ flight }) {
  return (
    <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-white/30 shadow-lg hover:shadow-xl transition-all p-5 md:p-6"
      data-testid={`flight-card-${flight.id}`}>
      <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-6">
        <div className="flex items-center gap-3 md:w-40 shrink-0">
          <img src={flight.airline.logo} alt={flight.airline.name}
            className="w-9 h-9 rounded-lg bg-ocean/10 object-contain p-1"
            onError={e => { e.target.style.display = 'none'; }} />
          <div>
            <p className="text-ocean text-sm font-medium">{flight.airline.name}</p>
            <p className="text-ocean/40 text-xs">{flight.flight_number}</p>
          </div>
        </div>

        <div className="flex items-center gap-4 flex-1 min-w-0">
          <div className="text-right">
            <p className="text-ocean text-xl font-semibold">{flight.departure_time}</p>
            <p className="text-ocean/40 text-xs truncate">{flight.origin}</p>
          </div>
          <div className="flex-1 flex flex-col items-center px-2">
            <p className="text-ocean/40 text-xs mb-1">{flight.duration_display}</p>
            <div className="w-full flex items-center gap-1">
              <div className="h-px flex-1 bg-ocean/20" />
              {flight.stops === 0 ? (
                <Plane className="w-3.5 h-3.5 text-gold -rotate-45" />
              ) : (
                Array.from({ length: flight.stops }).map((_, i) => (
                  <div key={`stop-${i}`} className="w-1.5 h-1.5 rounded-full bg-gold" />
                ))
              )}
              <div className="h-px flex-1 bg-ocean/20" />
            </div>
            <p className="text-ocean/40 text-xs mt-1">
              {flight.stops === 0 ? 'Non-stop' : `${flight.stops} stop${flight.stops > 1 ? 's' : ''}`}
              {flight.stop_cities?.length > 0 && ` via ${flight.stop_cities.join(', ')}`}
            </p>
          </div>
          <div>
            <p className="text-ocean text-xl font-semibold">
              {flight.arrival_time}
              {flight.next_day_arrival && <span className="text-gold text-xs ml-1 align-super">+1</span>}
            </p>
            <p className="text-ocean/40 text-xs truncate">{flight.destination}</p>
          </div>
        </div>

        <div className="flex items-center gap-4 md:flex-col md:items-end md:gap-2 md:w-44 shrink-0">
          <div className="text-right">
            <p className="text-ocean text-2xl font-bold">${flight.price.toLocaleString()}</p>
            <div className="flex items-center gap-2 justify-end">
              {flight.seats_left && flight.seats_left <= 5 && (
                <span className="text-red-500 text-xs">{flight.seats_left} left</span>
              )}
              <span className="text-ocean/40 text-xs flex items-center gap-1">
                <Luggage className="w-3 h-3" />{flight.baggage?.checked}
              </span>
            </div>
          </div>
          <a href={flight.booking_link} target="_blank" rel="noopener noreferrer"
            className="bg-gradient-to-r from-ocean to-sky text-white font-semibold rounded-full px-5 py-2 hover:shadow-lg transition-all text-sm flex items-center gap-1.5 whitespace-nowrap"
            data-testid="flight-book-btn">
            Book on Kiwi <ExternalLink className="w-3.5 h-3.5" />
          </a>
        </div>
      </div>
    </div>
  );
}

export function FlightFilters({ sortBy, setSortBy, filterStops, setFilterStops, onClose }) {
  const sortOptions = [
    { value: 'price_asc', label: 'Price: Low to High' },
    { value: 'price_desc', label: 'Price: High to Low' },
    { value: 'duration', label: 'Duration: Shortest' },
    { value: 'departure', label: 'Departure: Earliest' },
  ];

  const stopOptions = [
    { value: 'any', label: 'Any' },
    { value: 'nonstop', label: 'Non-stop only' },
    { value: '1stop', label: 'Up to 1 stop' },
    { value: '2stops', label: 'Up to 2 stops' },
  ];

  const RadioOption = ({ name, value, checked, onChange, label, shape = 'rounded-full' }) => (
    <label className="flex items-center gap-2 py-1.5 cursor-pointer group">
      <div className={`w-4 h-4 ${shape} border-2 flex items-center justify-center transition-colors ${checked ? 'border-ocean bg-ocean' : 'border-ocean/30 group-hover:border-ocean/50'}`}>
        {checked && <div className={`${shape === 'rounded-full' ? 'w-1.5 h-1.5 rounded-full' : 'w-2 h-2 rounded-sm'} bg-white`} />}
      </div>
      <span className="text-ocean/70 text-sm group-hover:text-ocean transition-colors">{label}</span>
      <input type="radio" name={name} value={value} checked={checked} onChange={onChange} className="hidden" />
    </label>
  );

  return (
    <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-white/30 shadow-xl p-6 sticky top-24" data-testid="flight-filters">
      <div className="flex items-center justify-between mb-5">
        <h3 className="font-serif text-lg text-ocean">Filters</h3>
        {onClose && (
          <button onClick={onClose} className="lg:hidden text-ocean/40 hover:text-ocean">
            <span className="sr-only">Close</span>&times;
          </button>
        )}
      </div>
      <div className="mb-6">
        <p className="text-xs tracking-wider uppercase text-ocean/60 font-semibold mb-3">Sort By</p>
        {sortOptions.map(opt => (
          <RadioOption key={opt.value} name="sort" value={opt.value} checked={sortBy === opt.value}
            onChange={() => setSortBy(opt.value)} label={opt.label} />
        ))}
      </div>
      <div>
        <p className="text-xs tracking-wider uppercase text-ocean/60 font-semibold mb-3">Stops</p>
        {stopOptions.map(opt => (
          <RadioOption key={opt.value} name="stops" value={opt.value} checked={filterStops === opt.value}
            onChange={() => setFilterStops(opt.value)} label={opt.label} shape="rounded" />
        ))}
      </div>
    </div>
  );
}

export function PopularRoutes({ routes, onSelect }) {
  return (
    <div data-testid="popular-routes">
      <h2 className="font-serif text-2xl text-white drop-shadow-lg mb-6">Popular Routes</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {routes.map((route, i) => (
          <button key={`route-${route.origin}-${route.destination}`} onClick={() => onSelect(route)}
            className="group relative overflow-hidden rounded-2xl border border-white/20 hover:border-gold/50 transition-all text-left shadow-lg"
            data-testid={`popular-route-${i}`}>
            <img src={route.image} alt={route.destination} className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-500" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-5">
              <div className="flex items-center gap-2 text-white text-lg font-semibold">
                {route.origin} <ArrowRight className="w-4 h-4 text-gold" /> {route.destination}
              </div>
              <p className="text-gold text-sm font-medium mt-1">From ${route.price_from}</p>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

export function FlightCompensation() {
  return (
    <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-white/30 shadow-xl overflow-hidden" data-testid="flight-compensation-section">
      <div className="bg-gradient-to-r from-gold/15 to-ocean/10 p-6 md:p-8">
        <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-gold to-gold-light flex items-center justify-center flex-shrink-0">
            <Plane className="w-7 h-7 text-white" />
          </div>
          <div className="flex-1">
            <h3 className="font-serif text-xl text-ocean mb-1">Flight Delayed or Cancelled?</h3>
            <p className="text-ocean/70 text-sm mb-4">
              You could be entitled to up to <span className="font-bold text-gold">$600</span> in compensation.
              Check your eligibility in 2 minutes — no upfront costs.
            </p>
            <div className="flex flex-wrap gap-3">
              <a href="https://airhelp.tpm.lv/PWwYaq5O" target="_blank" rel="noopener noreferrer">
                <button className="bg-gradient-to-r from-ocean to-sky text-white rounded-full px-6 h-10 font-semibold shadow-md hover:shadow-lg transition-all text-sm inline-flex items-center" data-testid="airhelp-btn">
                  Check with AirHelp <ExternalLink className="w-3.5 h-3.5 ml-1.5" />
                </button>
              </a>
              <a href="https://compensair.tpm.lv/ZOWHHas3" target="_blank" rel="noopener noreferrer">
                <button className="border border-ocean text-ocean hover:bg-ocean hover:text-white rounded-full px-6 h-10 font-semibold transition-all text-sm inline-flex items-center" data-testid="compensair-btn">
                  Check with Compensair <ExternalLink className="w-3.5 h-3.5 ml-1.5" />
                </button>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function TravelServices() {
  return (
    <div>
      <h2 className="font-serif text-2xl text-white drop-shadow-lg mb-5">Complete Your Travel</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <a href="https://tpm.lv/sD3CoTaI" target="_blank" rel="noopener noreferrer"
          className="group bg-white/95 backdrop-blur-xl rounded-2xl border border-white/30 shadow-lg p-6 hover:shadow-xl transition-all hover:border-gold/40"
          data-testid="welcome-pickups-flights">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center flex-shrink-0">
              <ArrowRight className="w-6 h-6 text-white rotate-45" />
            </div>
            <div className="flex-1">
              <h3 className="text-ocean font-semibold mb-1">Airport Pickup Service</h3>
              <p className="text-ocean/60 text-xs mb-3">Professional drivers waiting at the airport. Pre-book your pickup for a stress-free arrival at your destination.</p>
              <span className="inline-flex items-center gap-1.5 text-green-600 font-semibold text-sm group-hover:text-green-700 transition-colors">
                Book on Welcome Pickups <ExternalLink className="w-3.5 h-3.5" />
              </span>
            </div>
          </div>
        </a>
        <a href="https://gettransfer.tpm.lv/CDo528tq" target="_blank" rel="noopener noreferrer"
          className="group bg-white/95 backdrop-blur-xl rounded-2xl border border-white/30 shadow-lg p-6 hover:shadow-xl transition-all hover:border-gold/40"
          data-testid="get-transfer-flights">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-ocean to-sky flex items-center justify-center flex-shrink-0">
              <ArrowRight className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <h3 className="text-ocean font-semibold mb-1">Private Transfers</h3>
              <p className="text-ocean/60 text-xs mb-3">Door-to-door private transfers between cities, airports, and hotels. Compare driver offers and book instantly.</p>
              <span className="inline-flex items-center gap-1.5 text-ocean font-semibold text-sm group-hover:text-sky transition-colors">
                Book on GetTransfer <ExternalLink className="w-3.5 h-3.5" />
              </span>
            </div>
          </div>
        </a>
      </div>
    </div>
  );
}
